'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';
import { RefreshCw, Mail, Clock, FileText, Sparkles, User } from 'lucide-react';

type InterviewType = 'Phone Screen' | 'Video' | 'In-Person' | 'Panel' | 'Technical';
type Tone = 'Professional' | 'Warm' | 'Enthusiastic';

interface GeneratedEmail {
  subject: string;
  body: string;
  variationIndex: number;
}

// --- Subject line templates ---

const subjectTemplates: Record<Tone, string[]> = {
  Professional: [
    'Thank You for the Interview - {jobTitle} Position',
    'Following Up: {jobTitle} Interview at {company}',
    'Thank You for Your Time Today - {jobTitle} Role',
    'Appreciation for the {jobTitle} Interview',
    'Thank You - {jobTitle} Position Discussion',
    'Following Up on Our {jobTitle} Conversation',
  ],
  Warm: [
    'Great Speaking with You About the {jobTitle} Role',
    'Thank You for the Wonderful Conversation - {jobTitle}',
    'Grateful for Today\'s {jobTitle} Interview',
    'Enjoyed Our Conversation About the {jobTitle} Position',
    'Thank You for the Opportunity to Interview - {jobTitle}',
    'A Sincere Thank You - {jobTitle} Interview',
  ],
  Enthusiastic: [
    'Excited About the {jobTitle} Opportunity - Thank You!',
    'Thrilled to Interview for {jobTitle} - Thank You!',
    'Thank You! More Excited Than Ever About {jobTitle}',
    'Energized After Our {jobTitle} Discussion - Thank You!',
    'Can\'t Wait to Potentially Join {company} - Thank You!',
    'Inspired by Our Conversation - {jobTitle} at {company}',
  ],
};

// --- Body templates ---

function getBodyTemplates(
  interviewer: string,
  interviewerTitle: string,
  company: string,
  jobTitle: string,
  specificDetail: string,
  interviewType: InterviewType,
  tone: Tone
): string[] {
  const interviewTypeDescriptor: Record<InterviewType, string> = {
    'Phone Screen': 'speaking with you on the phone',
    'Video': 'meeting with you over video',
    'In-Person': 'meeting with you in person',
    'Panel': 'meeting with you and the team',
    'Technical': 'the technical discussion we had',
  };

  const meetingDesc = interviewTypeDescriptor[interviewType];

  const specificSentence = specificDetail
    ? `I especially enjoyed our discussion about ${specificDetail}. It gave me a much clearer picture of the role and reinforced my enthusiasm for the opportunity.`
    : `Our conversation gave me valuable insight into the role and the team, and it reinforced my enthusiasm for the opportunity.`;

  const specificSentenceWarm = specificDetail
    ? `I particularly loved hearing about ${specificDetail} -- it really resonated with me and made me even more excited about what this role could look like.`
    : `Learning more about the team and the vision for this role made me even more excited about what the future could look like at ${company}.`;

  const specificSentenceEnthusiastic = specificDetail
    ? `I was genuinely thrilled when we discussed ${specificDetail}. It is exactly the kind of challenge and opportunity I have been looking for, and I could not stop thinking about it after our conversation.`
    : `Everything I learned about the role and ${company}'s direction has me genuinely excited about the possibility of contributing to such meaningful work.`;

  const professionalTemplates: string[] = [
    // Template 1
`Dear ${interviewer},

Thank you for taking the time to ${meetingDesc} today regarding the ${jobTitle} position at ${company}. I sincerely appreciate the opportunity to learn more about the role and your team.

${specificSentence}

My background in this area aligns well with what you are looking for, and I am confident I could make a meaningful contribution to ${company}. I am very interested in the next steps and would welcome the chance to continue the conversation.

Please do not hesitate to reach out if you need any additional information from me. Thank you again for your time and consideration.

Best regards,
[Your Name]`,

    // Template 2
`Dear ${interviewer},

I wanted to express my sincere gratitude for ${meetingDesc} today to discuss the ${jobTitle} position. It was a pleasure to learn about the work your team is doing at ${company}.

${specificSentence}

I believe my skills and experience would be a strong fit for this role, and I am eager to bring my expertise to your team. I look forward to hearing about the next steps in the process.

Thank you again for the opportunity.

Sincerely,
[Your Name]`,

    // Template 3
`Dear ${interviewer},

Thank you for the opportunity to interview for the ${jobTitle} role at ${company} today. I appreciate you sharing details about the position and what the team is working on.

${specificSentence}

After our conversation, I am even more convinced that my experience and skills are well-suited to this role. I would welcome the opportunity to contribute to ${company}'s continued success.

I look forward to hearing from you regarding the next steps. Please feel free to contact me if you require any further information.

With appreciation,
[Your Name]`,

    // Template 4
`Dear ${interviewer},

I appreciate you taking the time to discuss the ${jobTitle} position with me today. ${meetingDesc.charAt(0).toUpperCase() + meetingDesc.slice(1)} was a valuable experience, and I left our conversation with a strong understanding of the role and its responsibilities.

${specificSentence}

I am confident that my qualifications align well with the needs of your team, and I remain very interested in this opportunity. I am available at your convenience should you need any additional information.

Thank you for your time and consideration.

Respectfully,
[Your Name]`,

    // Template 5
`Dear ${interviewer},

Thank you for ${meetingDesc} to discuss the ${jobTitle} opportunity at ${company}. I value the time you spent walking me through the role and what your team is focused on.

${specificSentence}

The more I learn about this position, the more certain I am that it is an excellent match for my background and career goals. I am ready to hit the ground running and make an immediate impact.

I look forward to the possibility of joining your team. Thank you again for your consideration.

Kind regards,
[Your Name]`,

    // Template 6
`Dear ${interviewer},

I wanted to follow up and thank you for the opportunity to interview for the ${jobTitle} role today. I enjoyed ${meetingDesc} and learning more about the direction of the team at ${company}.

${specificSentence}

I am very enthusiastic about this opportunity and believe I could contribute meaningfully to your team's goals. Please do not hesitate to reach out if there is anything else I can provide.

Thank you once again for your time.

Best,
[Your Name]`,
  ];

  const warmTemplates: string[] = [
    // Template 1
`Dear ${interviewer},

I just wanted to send a quick note to say thank you so much for ${meetingDesc} today. I truly enjoyed our conversation about the ${jobTitle} role at ${company}, and I came away feeling really positive about the opportunity.

${specificSentenceWarm}

I genuinely believe this role would be a wonderful fit, and I would love the chance to bring my skills and energy to your team. Please do not hesitate to reach out if there is anything else I can share.

I look forward to hearing from you, and thanks again for such a great conversation.

Warmly,
[Your Name]`,

    // Template 2
`Dear ${interviewer},

Thank you so much for taking the time to chat with me today about the ${jobTitle} position. I really appreciated your openness in sharing what it is like to work at ${company}, and it was wonderful ${meetingDesc}.

${specificSentenceWarm}

I can honestly say that this is a role I feel genuinely passionate about. I believe my background would allow me to contribute to the great work your team is already doing, and I would be thrilled to be part of it.

Thanks again for the lovely conversation. I hope we get the chance to work together.

All the best,
[Your Name]`,

    // Template 3
`Dear ${interviewer},

I wanted to reach out right away to thank you for ${meetingDesc} today. Our conversation about the ${jobTitle} position at ${company} was one of the best interview experiences I have had, and that is entirely thanks to you.

${specificSentenceWarm}

I left our conversation feeling inspired and hopeful about what this role could be. I would be so grateful for the opportunity to join the team and contribute to ${company}'s mission.

Thank you again for your time and warmth. I look forward to hearing from you.

With gratitude,
[Your Name]`,

    // Template 4
`Dear ${interviewer},

What a wonderful conversation! Thank you for ${meetingDesc} to discuss the ${jobTitle} role. I really appreciated how candid and welcoming you were throughout our discussion.

${specificSentenceWarm}

I can see myself thriving in this role and being a great addition to the ${company} team. The culture you described is exactly the kind of environment where I do my best work.

I truly appreciate the time you took today, and I look forward to what comes next.

Warm regards,
[Your Name]`,

    // Template 5
`Dear ${interviewer},

I cannot thank you enough for ${meetingDesc} today to talk about the ${jobTitle} position. Your insights into the role and ${company}'s culture were incredibly helpful, and I genuinely enjoyed getting to know you a bit.

${specificSentenceWarm}

This opportunity feels like a natural next step for me, and I would love the chance to contribute to the meaningful work your team is doing. Please feel free to reach out anytime if you need additional information from my end.

Thank you for making today such a positive experience.

Take care,
[Your Name]`,

    // Template 6
`Dear ${interviewer},

A heartfelt thank you for the time you spent with me today discussing the ${jobTitle} role at ${company}. I really valued ${meetingDesc} and learning about the exciting work ahead for the team.

${specificSentenceWarm}

I came away from our meeting feeling that this position is a wonderful match for my experience and interests. The way you described the team dynamic made me even more eager to be part of it.

Thank you again for the wonderful conversation. I sincerely hope to have the opportunity to work with you.

With appreciation,
[Your Name]`,
  ];

  const enthusiasticTemplates: string[] = [
    // Template 1
`Dear ${interviewer},

Wow -- thank you so much for ${meetingDesc} today! I am absolutely thrilled about the ${jobTitle} opportunity at ${company}, and our conversation only made me more excited.

${specificSentenceEnthusiastic}

I walked away from our conversation buzzing with ideas about how I could contribute to the team. My skills and passion align perfectly with what you are building, and I would be honored to be part of it.

I cannot wait to hear about the next steps. Thank you again for such an energizing and inspiring conversation!

Excitedly,
[Your Name]`,

    // Template 2
`Dear ${interviewer},

Thank you, thank you, thank you for taking the time to interview me for the ${jobTitle} role today! ${meetingDesc.charAt(0).toUpperCase() + meetingDesc.slice(1)} was an incredible experience, and I am more motivated than ever to join ${company}.

${specificSentenceEnthusiastic}

I have been looking for exactly this kind of opportunity -- one where I can make a real impact while growing alongside a talented team. I truly believe this is it, and I am ready to bring my best every single day.

Thank you again for such an amazing conversation. I am eagerly looking forward to the next steps!

With excitement,
[Your Name]`,

    // Template 3
`Dear ${interviewer},

I just had to reach out immediately to say how grateful and excited I am after our conversation today! ${meetingDesc.charAt(0).toUpperCase() + meetingDesc.slice(1)} about the ${jobTitle} role at ${company} was easily the highlight of my week.

${specificSentenceEnthusiastic}

I cannot overstate how well this role matches what I have been looking for. The opportunity to work with you and the team would be a dream, and I am fully prepared to hit the ground running from day one.

Thank you for your time and for getting me so fired up about this opportunity!

Best,
[Your Name]`,

    // Template 4
`Dear ${interviewer},

I am still smiling after our conversation today -- thank you so much for ${meetingDesc} to discuss the ${jobTitle} position! The energy and vision you described at ${company} are absolutely contagious.

${specificSentenceEnthusiastic}

I left our meeting with a notebook full of ideas and an overwhelming sense that this is where I am meant to be. My experience and drive would be a perfect match for what you are looking for, and I am itching to get started.

I so appreciate the time you invested in me today. I will be looking forward to hearing from you with great anticipation!

Cheers,
[Your Name]`,

    // Template 5
`Dear ${interviewer},

What an amazing conversation! Thank you so much for ${meetingDesc} today about the ${jobTitle} opportunity at ${company}. I am beyond excited about the possibility of joining your team.

${specificSentenceEnthusiastic}

Every detail you shared made me more certain that this role is the perfect fit. I bring a combination of skills, passion, and determination that I know would make a real difference, and I am chomping at the bit to prove it.

Thank you for making this interview such an incredible experience. I am truly looking forward to what is next!

With enthusiasm,
[Your Name]`,

    // Template 6
`Dear ${interviewer},

I could not wait to send this note -- thank you for ${meetingDesc} today to discuss the ${jobTitle} role! Meeting with you and learning about ${company}'s vision was absolutely inspiring.

${specificSentenceEnthusiastic}

This is the kind of opportunity that gets me out of bed in the morning. I am confident that my background, energy, and passion make me a strong fit, and I would love nothing more than to contribute to the incredible things happening at ${company}.

Thank you for an unforgettable interview experience. I am keeping my fingers crossed and looking forward to the next steps!

Can't wait,
[Your Name]`,
  ];

  const templateMap: Record<Tone, string[]> = {
    Professional: professionalTemplates,
    Warm: warmTemplates,
    Enthusiastic: enthusiasticTemplates,
  };

  return templateMap[tone];
}

function generateEmail(
  interviewer: string,
  interviewerTitle: string,
  company: string,
  jobTitle: string,
  specificDetail: string,
  interviewType: InterviewType,
  tone: Tone,
  variationIndex?: number
): GeneratedEmail {
  const subjects = subjectTemplates[tone];
  const bodies = getBodyTemplates(
    interviewer || '[Interviewer Name]',
    interviewerTitle || '[Their Title]',
    company || '[Company Name]',
    jobTitle || '[Job Title]',
    specificDetail,
    interviewType,
    tone
  );

  const idx = variationIndex !== undefined ? variationIndex : Math.floor(Math.random() * Math.min(subjects.length, bodies.length));
  const safeIdx = idx % Math.min(subjects.length, bodies.length);

  const subject = subjects[safeIdx]
    .replace(/\{jobTitle\}/g, jobTitle || '[Job Title]')
    .replace(/\{company\}/g, company || '[Company Name]');

  return {
    subject,
    body: bodies[safeIdx],
    variationIndex: safeIdx,
  };
}

export default function ThankYouEmailContent() {
  const [interviewer, setInterviewer] = useState('');
  const [interviewerTitle, setInterviewerTitle] = useState('');
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [specificDetail, setSpecificDetail] = useState('');
  const [interviewType, setInterviewType] = useState<InterviewType>('In-Person');
  const [tone, setTone] = useState<Tone>('Professional');
  const [email, setEmail] = useState<GeneratedEmail | null>(null);
  const [generated, setGenerated] = useState(false);

  const interviewTypes: InterviewType[] = ['Phone Screen', 'Video', 'In-Person', 'Panel', 'Technical'];
  const tones: Tone[] = ['Professional', 'Warm', 'Enthusiastic'];

  const handleGenerate = () => {
    const result = generateEmail(
      interviewer,
      interviewerTitle,
      company,
      jobTitle,
      specificDetail,
      interviewType,
      tone
    );
    setEmail(result);
    setGenerated(true);
  };

  const handleRegenerate = () => {
    if (!email) return;
    // Pick a different variation
    let newIdx = Math.floor(Math.random() * 6);
    while (newIdx === email.variationIndex) {
      newIdx = Math.floor(Math.random() * 6);
    }
    const result = generateEmail(
      interviewer,
      interviewerTitle,
      company,
      jobTitle,
      specificDetail,
      interviewType,
      tone,
      newIdx
    );
    setEmail(result);
  };

  const handleToneChange = (newTone: Tone) => {
    setTone(newTone);
    if (generated) {
      const result = generateEmail(
        interviewer,
        interviewerTitle,
        company,
        jobTitle,
        specificDetail,
        interviewType,
        newTone
      );
      setEmail(result);
    }
  };

  const fullEmailText = email ? `Subject: ${email.subject}\n\n${email.body}` : '';

  const inputClass =
    'w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow text-gray-900';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Thank You Email Generator"
        description="Create a personalized post-interview thank you email in seconds. Choose your tone and customize for any interview scenario."
        category="Interview Tools"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Form */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Interview Details</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Interviewer Name */}
            <div>
              <label htmlFor="interviewer" className={labelClass}>
                Interviewer&apos;s Name
              </label>
              <input
                id="interviewer"
                type="text"
                placeholder="Sarah Johnson"
                value={interviewer}
                onChange={(e) => setInterviewer(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Interviewer Title/Role */}
            <div>
              <label htmlFor="interviewerTitle" className={labelClass}>
                Their Title / Role
              </label>
              <input
                id="interviewerTitle"
                type="text"
                placeholder="Engineering Manager"
                value={interviewerTitle}
                onChange={(e) => setInterviewerTitle(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Company Name */}
            <div>
              <label htmlFor="company" className={labelClass}>
                Company Name
              </label>
              <input
                id="company"
                type="text"
                placeholder="Acme Corp"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Job Title */}
            <div>
              <label htmlFor="jobTitle" className={labelClass}>
                Job Title You Interviewed For
              </label>
              <input
                id="jobTitle"
                type="text"
                placeholder="Senior Software Engineer"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Interview Type */}
            <div>
              <label htmlFor="interviewType" className={labelClass}>
                Interview Type
              </label>
              <select
                id="interviewType"
                value={interviewType}
                onChange={(e) => setInterviewType(e.target.value as InterviewType)}
                className={inputClass}
              >
                {interviewTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {/* Tone */}
            <div>
              <label htmlFor="tone" className={labelClass}>
                Tone
              </label>
              <select
                id="tone"
                value={tone}
                onChange={(e) => handleToneChange(e.target.value as Tone)}
                className={inputClass}
              >
                {tones.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {/* Specific Detail */}
            <div className="sm:col-span-2">
              <label htmlFor="specificDetail" className={labelClass}>
                Something Specific You Discussed{' '}
                <span className="font-normal text-gray-400">(optional -- makes it more personal)</span>
              </label>
              <input
                id="specificDetail"
                type="text"
                placeholder="e.g., the upcoming product launch, migrating to microservices, the team's mentorship culture"
                value={specificDetail}
                onChange={(e) => setSpecificDetail(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Generate Button */}
          <div className="mt-8">
            <button
              onClick={handleGenerate}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-10 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-lg"
            >
              Generate Email
            </button>
          </div>
        </div>

        {/* Results Section */}
        {generated && email && (
          <div className="mt-10">
            {/* Tone Tabs + Actions */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {tones.map((t) => (
                <button
                  key={t}
                  onClick={() => handleToneChange(t)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                    tone === t
                      ? 'bg-blue-600 text-white shadow'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={handleRegenerate}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Regenerate
                </button>
                <CopyButton text={fullEmailText} label="Copy All" />
              </div>
            </div>

            {/* Email Preview Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Subject Line */}
              <div className="border-b border-gray-100 px-8 py-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Subject Line</p>
                    <p className="text-lg font-semibold text-gray-900">{email.subject}</p>
                  </div>
                  <CopyButton text={email.subject} label="Copy" className="shrink-0 mt-1" />
                </div>
              </div>

              {/* Email Body */}
              <div className="px-8 py-6 sm:px-12 sm:py-8">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Email Body</p>
                  <CopyButton text={email.body} label="Copy Body" />
                </div>
                <div className="font-serif text-gray-800 leading-relaxed whitespace-pre-line text-base sm:text-lg">
                  {email.body}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tips Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Thank You Email Best Practices</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Send Within 24 Hours</h3>
                <p className="text-sm text-gray-600">
                  The sooner the better. Ideally, send your thank you email the same day as your interview, or by the next morning at the latest. This keeps you top of mind while the conversation is still fresh.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Keep It Concise</h3>
                <p className="text-sm text-gray-600">
                  Your email should be short and impactful -- around 150 to 250 words. Hiring managers are busy, so get to the point while still being genuine and memorable.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Personalize It</h3>
                <p className="text-sm text-gray-600">
                  Reference something specific from your conversation -- a project, a challenge, or even something personal the interviewer shared. This shows you were truly engaged and listening.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">One Email Per Interviewer</h3>
                <p className="text-sm text-gray-600">
                  If you met with multiple people (such as in a panel interview), send a separate, slightly different thank you email to each person. Do not send a generic group message.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Proofread Carefully</h3>
                <p className="text-sm text-gray-600">
                  Spelling or grammar mistakes in a thank you email can undo the positive impression you made. Read it aloud before sending, and double-check the interviewer&apos;s name and company name.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Reiterate Your Interest</h3>
                <p className="text-sm text-gray-600">
                  Use the email as a chance to reaffirm your enthusiasm for the role. Briefly mention why you are excited and how your skills align with what they need. End with a clear, positive close.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Educational Content */}
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Thank You Emails Matter More Than You Think</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Sending a thank you email after an interview is one of the simplest yet most impactful steps you can take in your job search. According to hiring managers surveyed by TopResume, <a href="https://www.indeed.com/career-advice/interviewing/thank-you-email-after-interview" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">68% of interviewers say a thank you note influences their hiring decision</a>. Despite this, many candidates skip this step entirely, giving you a real competitive edge when you follow through.
            </p>
            <p>
              A strong thank you email does more than express gratitude. It reinforces your interest in the position, gives you a chance to address anything you may have forgotten to mention during the interview, and demonstrates the kind of professionalism and attention to detail that employers value. It also keeps your name top of mind during the decision-making process, which can make all the difference when the choice comes down to two equally qualified candidates.
            </p>
            <p>
              The ideal thank you email is sent within 24 hours of the interview, is concise (under 200 words), and includes at least one specific detail from the conversation. This shows that you were genuinely engaged and listening, not just going through the motions. For panel interviews, send individualized notes to each interviewer rather than a single generic message.
            </p>
            <h4 className="text-lg font-bold text-gray-900 pt-2">Common Mistakes to Avoid</h4>
            <ul className="list-disc pl-5 space-y-2">
              <li>Sending a generic, copy-paste message that could apply to any company or role. Personalization is what makes your email memorable.</li>
              <li>Waiting more than 48 hours to send your note. By then, the interviewer may have already moved forward with other candidates.</li>
              <li>Writing an overly long email that rehashes the entire interview. Keep it focused and respectful of the reader&apos;s time.</li>
              <li>Forgetting to proofread. A typo in the interviewer&apos;s name or the company name can undo the positive impression you made.</li>
            </ul>
            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.indeed.com/career-advice/interviewing/thank-you-email-after-interview" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: How to Write a Thank You Email After an Interview</a>
              {' · '}
              <a href="https://hbr.org/2022/11/how-to-write-a-thank-you-email-after-a-job-interview" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: How to Write a Thank You After an Interview</a>
              {' · '}
              <a href="https://www.shrm.org/topics-tools/news/talent-acquisition/write-thank-note-interview" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">SHRM: Writing Thank You Notes After Interviews</a>
            </p>
          </div>
        </section>

        {/* CTA */}
        <ToolCTA
          headline="Aced the interview? Get your resume ready for the next one."
          description="Use our AI-powered resume builder to craft a polished, ATS-friendly resume that highlights your strengths and helps you land more interviews."
          buttonText="Build My Resume Free"
          buttonHref="https://app.superpowerresume.com/sign-up"
        />
      </div>
    </div>
  );
}
